import React, { useCallback, useContext } from 'react'
import { useSelector } from 'react-redux'
import { useNewTranslation } from 'hooks'
import { STEP_TYPE_ENUM, TASK_TYPE_ENUM } from 'services/api/constants'
import { PredefinedContext } from './index'
import { theme } from 'theme'
import { BaseEditorContainer, EditorContentCardContainer } from './styles'
import { CAMPAIGN_STATUS_ENUM, selectEditingCampaign } from 'state/campaigns'
import { useField, useFormikContext } from 'formik'
import Text from 'particles/Text'
import BaseDivider from './BaseDivider'
import styled from 'particles/themes/styled'
import Field from 'components/Shared/Fields'
import CampaignEditorInputFieldContainer from 'components/Shared/Permission/CampaignEditorInputFieldContainer'
import Title from '../Shared/Title'
import Toggle from 'particles/Toggle'
import { snakeCaseToCamelCase } from 'components/utils'
import { fieldIdentifiers } from 'components/Shared/Comment'

interface TaskScheduleEditorProps {
    taskType: TASK_TYPE_ENUM
    className?: string
    taskUuid?: string
}
const TaskScheduleEditor: React.FC<TaskScheduleEditorProps> = ({ taskType, className, taskUuid }) => {
    const [t] = useNewTranslation()
    const { stepSettings, allStepsInTask, dynamicTaskState, showSchedulePostTime } = useContext(PredefinedContext)
    const [field] = useField<boolean>('data.add_post_time')
    const { setFieldValue } = useFormikContext()
    const config = stepSettings
    const allSteps = allStepsInTask
    const includedSteps = dynamicTaskState && dynamicTaskState.includedSteps
    const stepsToShow = allSteps.filter((stepType: string) => includedSteps[stepType])
    const campaign = useSelector(selectEditingCampaign)
    const isCampaignLive = Boolean(campaign && campaign.status === CAMPAIGN_STATUS_ENUM.LIVE)

    const getDateSubtitleLabel = useCallback(
        (taskType, stepType) => {
            let startDateKey = '',
                endDateKey = ''
            switch (taskType) {
                case TASK_TYPE_ENUM.PURCHASE_PRODUCT:
                    if (STEP_TYPE_ENUM.PURCHASE_PRODUCT === stepType) {
                        startDateKey = 'campaignEditor.createTask.enterStartDateProductPurchaseSubtext'
                        endDateKey = 'campaignEditor.createTask.enterEndDateProductPurchaseSubtext'
                    } else if (STEP_TYPE_ENUM.SUBMIT_PROOF_PHOTO === stepType) {
                        startDateKey = 'campaignEditor.createTask.enterStartDateSubmitProofPurchaseSubtext'
                        endDateKey = 'campaignEditor.createTask.enterEndDateSubmitProofPurchaseSubtext'
                    }
                    break
                case TASK_TYPE_ENUM.ATTEND_EVENT:
                    if (STEP_TYPE_ENUM.ATTEND_EVENT === stepType) {
                        startDateKey = 'campaignEditor.createTask.enterStartDateAttendEventSubtext'
                        endDateKey = 'campaignEditor.createTask.enterEndDateAttendEventSubtext'
                    } else if (STEP_TYPE_ENUM.SUBMIT_PROOF_PHOTO === stepType) {
                        startDateKey = 'campaignEditor.createTask.enterStartDateSubmitProofEventSubtext'
                        endDateKey = 'campaignEditor.createTask.enterEndDateSubmitProofEventSubtext'
                    }
                    break
                case TASK_TYPE_ENUM.IG_POST:
                case TASK_TYPE_ENUM.IG_POST_V02:
                case TASK_TYPE_ENUM.IG_STORY:
                case TASK_TYPE_ENUM.IG_STORY_V02:
                case TASK_TYPE_ENUM.IG_REEL:
                case TASK_TYPE_ENUM.FB_POST:
                case TASK_TYPE_ENUM.FB_POST_V02:
                case TASK_TYPE_ENUM.TIKTOK_POST:
                    switch (stepType) {
                        case STEP_TYPE_ENUM.SUBMIT_CONTENT_IG_POST:
                        case STEP_TYPE_ENUM.SUBMIT_CONTENT_IG_STORY:
                        case STEP_TYPE_ENUM.SUBMIT_CONTENT_IG_REEL:
                        case STEP_TYPE_ENUM.SUBMIT_CONTENT_FB_POST:
                        case STEP_TYPE_ENUM.SUBMIT_CONTENT_TIKTOK_POST:
                            startDateKey = 'campaignEditor.createTask.enterStartDateSubmitContentSubtext'
                            endDateKey = 'campaignEditor.createTask.enterEndDateSubmitContentSubtext'
                            break
                        case STEP_TYPE_ENUM.POST_AND_SUBMIT_PROOF_INSTAGRAM_POST_URL:
                        case STEP_TYPE_ENUM.POST_TO_IG_STORY:
                        case STEP_TYPE_ENUM.POST_AND_SUBMIT_PROOF_INSTAGRAM_REEL_URL:
                        case STEP_TYPE_ENUM.POST_AND_SUBMIT_PROOF_FACEBOOK_POST_URL:
                        case STEP_TYPE_ENUM.POST_AND_SUBMIT_PROOF_TIKTOK_POST_URL:
                        case STEP_TYPE_ENUM.POST_TO_FB_POST:
                        case STEP_TYPE_ENUM.POST_TO_IG_POST:
                            startDateKey = 'campaignEditor.createTask.enterStartDatePublishContentSubtext'
                            endDateKey = 'campaignEditor.createTask.enterEndDatePublishContentSubtext'
                            break
                        case STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_IG_POST:
                        case STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_IG_SCREENSHOT:
                        case STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_INSTAGRAM_REEL:
                        case STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_FACEBOOK_POST:
                        case STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_TIKTOK_POST:
                        case STEP_TYPE_ENUM.SUBMIT_PROOF_FB_URL:
                        case STEP_TYPE_ENUM.SUBMIT_PROOF_IG_SCREENSHOT:
                        case STEP_TYPE_ENUM.SUBMIT_PROOF_IG_URL:
                            startDateKey = 'campaignEditor.createTask.enterStartDateSubmitProofSubtext'
                            endDateKey = 'campaignEditor.createTask.enterEndDateSubmitProofSubtext'
                            break
                    }
                    break
            }

            return {
                startDateSubtitleLabel: t(startDateKey),
                endDateSubtitleLabel: t(endDateKey),
            }
        },
        [t],
    )

    return (
        <BaseEditorContainer className={className}>
            <EditorContentCardContainer>
                <div className="form-title">
                    <Text scale="H4" className="title" color="primary">
                        {t('campaignEditor.createTask.taskSchedulePageTitle')}
                    </Text>
                    <Text scale="bodyS400" color="secondary">
                        {t('campaignEditor.createTask.taskSchedulePageSubtitle')}
                    </Text>
                </div>
                <BaseDivider isFormTitleDivider />
                <div className="schedule-group">
                    {stepsToShow.map((stepType, index) => {
                        const needsReview = config[stepType].config.is_approval_required
                        const stepNumber = index + 1
                        const { startDateSubtitleLabel, endDateSubtitleLabel } = getDateSubtitleLabel(
                            taskType,
                            stepType,
                        )
                        const stepTypeIdentifier = snakeCaseToCamelCase(stepType)
                        return (
                            <div key={`${stepType}_${index}`} className="section">
                                <Title
                                    taskType={taskType}
                                    stepType={stepType}
                                    stepNumber={stepNumber}
                                    isScheduleLabel
                                />
                                {[
                                    STEP_TYPE_ENUM.POST_AND_SUBMIT_PROOF_INSTAGRAM_POST_URL,
                                    STEP_TYPE_ENUM.POST_TO_IG_POST,
                                    STEP_TYPE_ENUM.POST_AND_SUBMIT_PROOF_TIKTOK_POST_URL,
                                    STEP_TYPE_ENUM.POST_TO_FB_POST,
                                    STEP_TYPE_ENUM.POST_AND_SUBMIT_PROOF_FACEBOOK_POST_URL,
                                    STEP_TYPE_ENUM.POST_TO_IG_STORY,
                                    STEP_TYPE_ENUM.POST_AND_SUBMIT_PROOF_INSTAGRAM_REEL_URL,
                                ].includes(stepType) && showSchedulePostTime ? (
                                    <>
                                        <BaseDivider />
                                        <CampaignEditorInputFieldContainer isEditDeadline>
                                            <Toggle
                                                isChecked={field?.value}
                                                handleChange={() => {
                                                    setFieldValue(field.name as never, !field?.value)
                                                }}
                                                label={t('campaignEditor.createTask.turnOnScheduledPostHeader')}
                                                subtext={t('campaignEditor.createTask.turnOnScheduledPostSubtext')}
                                                identifier={`${taskUuid}_${stepTypeIdentifier}_${fieldIdentifiers.schedulePostToggle}`}
                                            />
                                        </CampaignEditorInputFieldContainer>
                                        <BaseDivider />
                                    </>
                                ) : null}
                                {campaign && (
                                    <CampaignEditorInputFieldContainer isEditDeadline launchFieldEditable>
                                        <Field.DatePickerField
                                            title={t('general.information.startDate')}
                                            subtitle={startDateSubtitleLabel}
                                            data-testid="test-date-picker"
                                            name={`stepData.${stepType}.time_window.start`}
                                            isRequired
                                            readOnlyForPastDate={isCampaignLive}
                                            country={campaign.country}
                                            identifier={`${taskUuid}_${stepTypeIdentifier}_${fieldIdentifiers.start}`}
                                        />
                                        <Field.DatePickerField
                                            title={t('general.information.endDate')}
                                            subtitle={endDateSubtitleLabel}
                                            name={`stepData.${stepType}.time_window.end`}
                                            isRequired
                                            country={campaign.country}
                                            identifier={`${taskUuid}_${stepTypeIdentifier}_${fieldIdentifiers.end}`}
                                        />
                                    </CampaignEditorInputFieldContainer>
                                )}

                                {needsReview && (
                                    <CampaignEditorInputFieldContainer isEditDeadline>
                                        <Field.NumberInputField
                                            title={t('campaignEditor.createTask.enterReviewDurationHeader')}
                                            subtitle={t('campaignEditor.createTask.enterReviewDurationSubtext')}
                                            country={campaign?.country}
                                            width="133px"
                                            name={`stepData.${stepType}.review_duration`}
                                            placeholder={'0'}
                                            isRequired
                                            identifier={`${taskUuid}_${stepTypeIdentifier}_${fieldIdentifiers.reviewDuration}`}
                                        />
                                    </CampaignEditorInputFieldContainer>
                                )}
                                {index + 1 !== stepsToShow.length && <BaseDivider />}
                            </div>
                        )
                    })}
                </div>
            </EditorContentCardContainer>
        </BaseEditorContainer>
    )
}

export default styled(TaskScheduleEditor)`
    width: 100%;
    min-height: 100%;
    height: fit-content;
    box-sizing: border-box;
    background-color: ${theme.newColors.neutral50};
    .step-title {
        margin: 0;
    }
    .schedule-group {
        display: flex;
        flex-direction: column;
        gap: ${theme.newSpacing.l};
        .toggle-switcher {
            margin-top: 0;
        }
    }
    .section {
        .divider {
            margin: 0;
        }
    }
`
